import { useState, useEffect, useCallback } from 'react';
import { fetchCustomerById } from '../api/customersApi';
import type { CustomerListItem } from '../types';

interface UseCustomerOptions {
  customerId: string | null;
  autoFetch?: boolean;
}

interface UseCustomerReturn {
  customer: CustomerListItem | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch a single customer by ID
 */
export function useCustomer({
  customerId,
  autoFetch = true,
}: UseCustomerOptions): UseCustomerReturn {
  const [customer, setCustomer] = useState<CustomerListItem | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadCustomer = useCallback(async () => {
    if (!customerId) {
      setCustomer(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const data = await fetchCustomerById(customerId);
      setCustomer(data);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to load customer';
      setError(message);
      console.error('[useCustomer] Error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    if (autoFetch && customerId) {
      loadCustomer();
    }
  }, [autoFetch, customerId, loadCustomer]);

  return {
    customer,
    isLoading,
    error,
    refetch: loadCustomer,
  };
}
